import { useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Modal from "../../../components/Modal";
import InputGroup from "../../../components/FormComponent/InputGroup";
import Button from "../../../components/Button";
import { httprequest } from "../../../data/api";

const ReferralProfile = ({ profileData, dashboardData, setCurrentView, setProfileData }) => {
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({
    first_name: "",
    last_name: "",
    email: ""
  });
  const [shareEmail, setShareEmail] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOpenEditProfile = () => {
    setEditProfileForm({
      first_name: profileData?.first_name || dashboardData?.first_name || "",
      last_name: profileData?.last_name || dashboardData?.last_name || "",
      email: profileData?.email || dashboardData?.email || ""
    });
    setIsEditProfileModalOpen(true);
  };

  const handleEditProfileChange = (e) => {
    const { name, value } = e.target;
    setEditProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditProfileSubmit = async () => {
    if (!editProfileForm.first_name || !editProfileForm.last_name || !editProfileForm.email) {
      toast.error("Please fill in all required fields");

      return;
    }
    setIsSubmitting(true);
    try {
      const response = await httprequest.patch("/api/v1/referrals/referrer/profile", editProfileForm);
      toast.success(response?.data?.message || "Profile updated successfully!");
      setIsEditProfileModalOpen(false);
      try {
        const profileRes = await httprequest.get("/api/v1/referrals/referrer/profile");
        setProfileData(profileRes?.data?.data || profileRes?.data);
      } catch (err) { /* ignore */ }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShareEmail = async () => {
    if (!shareEmail) {
      toast.error("Please enter an email address");

      return;
    }
    setIsSharing(true);
    try {
      const code = dashboardData?.referral_code || profileData?.referral_code;
      if (!code) {
        toast.error("Referral code not found.");
        setIsSharing(false);

        return;
      }
      const payload = { code, recipient_email: shareEmail };
      const res = await httprequest.post("/api/v1/referrals/send", payload);
      if (res.data && !res.data.error) {
        toast.success("Referral email sent successfully!");
        setShareEmail("");
      } else {
        toast.error(res.data?.message || "Failed to send referral email");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send referral email");
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <main className="ref-dash-main ref-profile-main">
      <div className="ref-settings-header">
        <span className="ref-back-link" onClick={() => setCurrentView("dashboard")}>
          <Icon icon="bx:chevron-left" /> Dashboard <span className="ref-back-divider" style={{ color: "#ccc", margin: "0 8px" }}>|</span> <span style={{ color: "#0065FF" }}>Profile</span>
        </span>
      </div>

      <div className="ref-profile-grid">
        {/* Left Card: Personal Information */}
        <div className="ref-settings-card ref-profile-card">
          <div className="ref-profile-card-header">
            <h3>Personal Information</h3>
            <Icon icon="fluent:edit-16-regular" className="ref-profile-edit-icon" onClick={handleOpenEditProfile} style={{ cursor: "pointer" }} />
          </div>
          <div className="ref-profile-card-body">
            <div className="ref-profile-avatar-container">
              <img src={`https://ui-avatars.com/api/?name=${profileData?.first_name || "User"}+${profileData?.last_name || ""}&background=random`} alt="Avatar" className="ref-profile-large-avatar" />
            </div>
            <div className="ref-profile-details">
              <div className="ref-profile-field">
                <span className="ref-profile-field-label">First Name</span>
                <span className="ref-profile-field-value">{profileData?.first_name || "James"}</span>
              </div>
              <div className="ref-profile-field">
                <span className="ref-profile-field-label">Last Name</span>
                <span className="ref-profile-field-value">{profileData?.last_name || "James"}</span>
              </div>
              <div className="ref-profile-field">
                <span className="ref-profile-field-label">Email</span>
                <span className="ref-profile-field-value">{profileData?.email || "Example@gmail.com"}</span>
              </div>
              <div className="ref-profile-field">
                <span className="ref-profile-field-label">Phone Number</span>
                <span className="ref-profile-field-value">{profileData?.phone || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Card: Referral Code */}
        <div className="ref-settings-card ref-profile-card">
          <div className="ref-profile-section">
            <h3>Referral code</h3>
            <p>You can share this link to your referal link by copying and sharing to anyone you want to refer</p>
            <div className="ref-profile-copy-container">
              <input type="text" readOnly value={profileData?.referral_link || dashboardData?.referral_link || "www.shelf21.com/ref/james"} />
              <button className="ref-profile-copy-btn" onClick={() => {
                const codeLink = profileData?.referral_link || dashboardData?.referral_link || "";
                if (codeLink) {
                  navigator.clipboard.writeText(codeLink);
                  toast.success("Copied to clipboard!");
                }
              }}
              >
                <Icon icon="fluent:copy-16-regular" />
              </button>
            </div>
          </div>

          <div className="ref-profile-section" style={{ marginTop: "24px" }}>
            <h3>Share via email (optional)</h3>
            <p>Send this referral directly to your school administrator</p>
            <div className="ref-profile-email-container">
              <input type="email" placeholder="Email address" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} />
              <button className="ref-profile-send-btn" onClick={handleShareEmail} disabled={isSharing}>
                {isSharing ? "..." : <Icon icon="fluent:send-28-filled" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isopen={isEditProfileModalOpen}
        closeModal={() => setIsEditProfileModalOpen(false)}
        title="Edit Profile"
        subtitle="Update your personal information"
        modalContent={
          <div className="ref-modal-form">
            <InputGroup
              label="First Name"
              required={true}
              inputType="text"
              inputName="first_name"
              value={editProfileForm.first_name}
              onChange={handleEditProfileChange}
              placeHolder="First Name"
            />
            <InputGroup
              label="Last Name"
              required={true}
              inputType="text"
              inputName="last_name"
              value={editProfileForm.last_name}
              onChange={handleEditProfileChange}
              placeHolder="Last Name"
            />
            <InputGroup
              label="Email Address"
              required={true}
              inputType="email"
              inputName="email"
              value={editProfileForm.email}
              onChange={handleEditProfileChange}
              placeHolder="Email Address"
            />
          </div>
        }
        modalFooter={
          <div className="ref-modal-footer">
            <Button
              buttonName="Cancel"
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              onClick={() => setIsEditProfileModalOpen(false)}
            />
            <Button
              buttonName={isSubmitting ? "Saving..." : "Save Changes"}
              variant="primary"
              buttonSize="full"
              color="btndefault"
              onClick={handleEditProfileSubmit}
              disabled={isSubmitting}
            />
          </div>
        }
      />
    </main>
  );
};

export default ReferralProfile;
